import { useId, useState } from 'react';

import { BookMarkedIcon, CircleHelpIcon, PlusIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { DisableToolWarningDialog } from '@/components/pages/MCPs/DisableToolWarningDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { Label } from '@/components/ui/label';
import { RichRadioGroup, RichRadioItem } from '@/components/ui/rich-radio';
import type useBrains from '@/hooks/useBrains';
import { getSearchStrategy, useEffectiveTools } from '@/hooks/useEffectiveTools';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Brain, PartialBrain, SearchStrategyMode, Tool } from '@/shared/types/brain';
import { Collection } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import {
  isToolReferencedInGuidelines,
  removeAllReferencesOfToolFromGuidelines,
} from '@/util/guidelineToolReferences';

type BrainsHook = ReturnType<typeof useBrains>;

type KnowledgeSettingsForm = {
  collectionId: string;
  mode: SearchStrategyMode;
  isSearchToolChecked: boolean;
};

type KnowledgeSettingsDialogProps = {
  brain: Brain;
  knowledgeBases: Collection[];
  updateBrainAsync: BrainsHook['updateBrainAsync'];
  patchBrainGuidelinesAsync: BrainsHook['patchBrainGuidelinesAsync'];
  onClose: () => void;
};

export const KnowledgeSettingsDialog = ({
  brain,
  knowledgeBases,
  updateBrainAsync,
  patchBrainGuidelinesAsync,
  onClose,
}: KnowledgeSettingsDialogProps) => {
  const { t } = useTranslation();
  const id = useId();
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { effectiveTools } = useEffectiveTools(brain);
  const savedStrategy = getSearchStrategy(brain);
  const isSearchToolStored = brain.tools?.includes(Tool.SEARCH_KNOWLEDGE_BASE) ?? false;

  const form = useForm<KnowledgeSettingsForm>({
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      collectionId: brain.collection_id ?? '',
      mode: savedStrategy.mode,
      isSearchToolChecked: isSearchToolStored,
    },
  });
  const [collectionId, mode, isSearchToolChecked] = useWatch({
    control: form.control,
    name: ['collectionId', 'mode', 'isSearchToolChecked'],
  });
  const setDirty = { shouldDirty: true };
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCleanupPending, setIsCleanupPending] = useState(false);

  const isPrefetch = mode === SearchStrategyMode.PREFETCH;
  const isFieldsDisabled = !canWrite || isSaving || isCleanupPending;

  const buildUpdate = (): PartialBrain => {
    const update: PartialBrain = { brain_id: brain.brain_id };
    if (collectionId !== (brain.collection_id ?? '')) {
      update.collection_id = collectionId || null;
    }
    if (collectionId && mode !== savedStrategy.mode) {
      update.search_strategy = { mode, max_results: savedStrategy.max_results };
    }
    if (collectionId && isPrefetch && isSearchToolChecked !== isSearchToolStored) {
      const currentTools = brain.tools ?? [];
      update.tools = isSearchToolChecked
        ? [...currentTools, Tool.SEARCH_KNOWLEDGE_BASE]
        : currentTools.filter((tool) => tool !== Tool.SEARCH_KNOWLEDGE_BASE);
    }
    return update;
  };

  const update = buildUpdate();
  const hasChanges = Object.keys(update).length > 1;

  const isSearchToolAvailableAfterSave = () => {
    const tools = update.tools ?? brain.tools ?? [];
    return (
      tools.includes(Tool.SEARCH_KNOWLEDGE_BASE) ||
      (Boolean(collectionId) && mode === SearchStrategyMode.ON_DEMAND)
    );
  };

  const removeReferences = async (guidelines: Brain['guidelines']) => {
    const patch = removeAllReferencesOfToolFromGuidelines(guidelines, Tool.SEARCH_KNOWLEDGE_BASE);
    if (Object.keys(patch).length > 0) {
      await patchBrainGuidelinesAsync({ brain_id: brain.brain_id, preventToast: true, ...patch });
    }
  };

  // Each mutation shows its own error toast; a failure keeps the dialog open for a retry.
  const save = async (shouldRemoveReferences: boolean) => {
    setIsSaving(true);
    try {
      let latestGuidelines = brain.guidelines;
      if (!isCleanupPending) {
        latestGuidelines = (await updateBrainAsync(update)).guidelines;
      }
      if (shouldRemoveReferences) {
        setIsCleanupPending(true);
        await removeReferences(latestGuidelines);
      }
    } catch {
      setIsSaving(false);
      return;
    }
    onClose();
  };

  const handleSubmit = () => {
    if (isCleanupPending) {
      save(true);
      return;
    }
    const dropsReferencedSearchTool =
      effectiveTools.includes(Tool.SEARCH_KNOWLEDGE_BASE) &&
      !isSearchToolAvailableAfterSave() &&
      isToolReferencedInGuidelines(brain.guidelines, Tool.SEARCH_KNOWLEDGE_BASE);

    if (dropsReferencedSearchTool) {
      setIsWarningOpen(true);
      return;
    }
    save(false);
  };

  const handleConfirmToolRemoval = () => {
    setIsWarningOpen(false);
    save(true);
  };

  const knowledgeBaseOptions = knowledgeBases.map((kb) => ({
    value: kb.collection_id,
    label: kb.name,
    icon: <BookMarkedIcon className="size-4 text-foreground-muted" />,
    externalLink: `/${slug}/knowledge-bases/${kb.collection_id}`,
    externalLinkTooltip: `${t('common.go_to')} ${kb.name}`,
  }));

  return (
    <>
      <FormDialog
        open
        contentClassName="sm:max-w-2xl"
        onOpenChange={(open) => !open && onClose()}
        preventClose={isSaving || isCleanupPending}
        cancelDisabled={isSaving || isCleanupPending}
        title={
          brain.collection_id
            ? t('ai_agents.knowledge_search.dialog_title_edit')
            : t('ai_agents.knowledge_search.dialog_title_connect')
        }
        description={t('ai_agents.knowledge_search.dialog_description')}
        onSubmit={handleSubmit}
        submitDisabled={!canWrite || isSaving || (!hasChanges && !isCleanupPending)}
        submitOnEnter
        isLoading={isSaving}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${id}-knowledge-base`}>{t('collections.collection')}</Label>
            <PopoverCombobox
              id={`${id}-knowledge-base`}
              options={knowledgeBaseOptions}
              value={collectionId}
              onChange={(value) => form.setValue('collectionId', value, setDirty)}
              triggerPlaceholder={t('common.select_dots')}
              disabled={isFieldsDisabled}
              contentClassName="w-(--radix-popover-trigger-width)"
              allowDeselect
            />
            {knowledgeBases.length === 0 && (
              <Button variant="link" asChild className="h-auto w-fit p-0">
                <Link to={`/${slug}/knowledge-bases`} onClick={onClose}>
                  <PlusIcon />
                  {t('common.create')}
                </Link>
              </Button>
            )}
          </div>

          {collectionId && (
            <div className="flex flex-col gap-2">
              <Label>{t('ai_agents.knowledge_search.title')}</Label>
              <RichRadioGroup
                value={mode}
                onValueChange={(value) =>
                  form.setValue('mode', value as SearchStrategyMode, setDirty)
                }
                disabled={isFieldsDisabled}
                aria-label={t('ai_agents.knowledge_search.title')}
                className="gap-3"
              >
                <div
                  className={cn(
                    'flex w-full flex-col rounded-lg border bg-background transition-colors',
                    isPrefetch
                      ? 'gap-1.5 border-informational bg-bg-info-soft'
                      : 'hover:bg-bg-muted-light'
                  )}
                >
                  <RichRadioItem
                    value={SearchStrategyMode.PREFETCH}
                    title={t('ai_agents.knowledge_search.prefetch')}
                    description={t('ai_agents.knowledge_search.prefetch_description')}
                    className={cn(
                      'w-full max-w-full border-0 bg-transparent hover:bg-transparent data-[state=checked]:bg-transparent',
                      isPrefetch && 'min-h-0 pb-0'
                    )}
                  />
                  {isPrefetch && (
                    <div className="flex items-center gap-2 px-4 pb-4 pl-7">
                      <Checkbox
                        id={`${id}-search-tool`}
                        checked={isSearchToolChecked}
                        disabled={isFieldsDisabled}
                        onCheckedChange={(checked) =>
                          form.setValue('isSearchToolChecked', checked === true, setDirty)
                        }
                      />
                      <Label htmlFor={`${id}-search-tool`} className="text-sm font-normal">
                        {t('ai_agents.knowledge_search.search_during_reply')}
                      </Label>
                      <InfoTooltip
                        content={t('ai_agents.knowledge_search.search_during_reply_description')}
                      >
                        <CircleHelpIcon size={12} className="text-foreground-muted" />
                      </InfoTooltip>
                    </div>
                  )}
                </div>
                <RichRadioItem
                  value={SearchStrategyMode.ON_DEMAND}
                  title={t('ai_agents.knowledge_search.on_demand')}
                  description={t('ai_agents.knowledge_search.on_demand_description')}
                  className="w-full max-w-full"
                />
              </RichRadioGroup>
            </div>
          )}
        </div>
      </FormDialog>

      <DisableToolWarningDialog
        open={isWarningOpen}
        onClose={() => setIsWarningOpen(false)}
        onConfirm={handleConfirmToolRemoval}
        onViewGuideline={onClose}
      />
    </>
  );
};
